import {
  findCitationField,
  groupedCitationFields,
} from "./citationFieldRegistry";
import {
  ArticleRounded as FilingIcon,
  AssignmentIndRounded as IncludeContextIcon,
  AttachFileRounded as AttachIcon,
  BookmarkBorderRounded as BookmarkOffIcon,
  BookmarkRounded as BookmarkOnIcon,
  CheckCircleRounded as CheckIcon,
  ChatRounded as FollowUpIcon,
  DeleteOutlineRounded as DeleteIcon,
  EditNoteRounded as NoteIcon,
  ExpandMoreRounded as ExpandMoreIcon,
  LanguageRounded as WebIcon,
  LinkRounded as LinkIcon,
  MoreVertRounded as MoreIcon,
  NotesRounded as ManualNoteIcon,
  OpenInNewRounded as OpenIcon,
  SendRounded as SendIcon,
  SmartToyRounded as AiIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import ReactMarkdown from "react-markdown";

const ItemTypeIcon = ({ itemType }) => {
  switch (itemType) {
    case "web_search":
      return <WebIcon fontSize={"small"} color={"primary"} />;
    case "ai_query":
      return <AiIcon fontSize={"small"} color={"secondary"} />;
    case "manual_note":
      return <ManualNoteIcon fontSize={"small"} color={"action"} />;
    case "reference_filing":
      return <FilingIcon fontSize={"small"} color={"info"} />;
    default:
      return <WebIcon fontSize={"small"} />;
  }
};

const ItemTypeLabel = ({ itemType, provider }) => {
  if (itemType === "web_search") return provider || "Web Search";
  if (itemType === "ai_query") return provider || "AI Research";
  if (itemType === "reference_filing") return "SEC Filing";
  return "Manual Note";
};

const CiteInFieldMenuItems = ({ citedIn, onToggle }) => {
  const groups = groupedCitationFields();
  const items = [];
  groups.forEach(({ group, fields }, groupIdx) => {
    items.push(
      <ListSubheader
        key={`hdr-${group}`}
        sx={{
          fontSize: "0.65rem",
          fontWeight: 700,
          textTransform: "uppercase",
          lineHeight: 1.6,
          py: 0,
          color: "text.secondary",
          bgcolor: "background.paper",
        }}
      >
        {group}
      </ListSubheader>
    );
    fields.forEach((field) => {
      const isOn = (citedIn || []).includes(field.key);
      items.push(
        <MenuItem
          key={field.key}
          onClick={() => onToggle(field.key)}
          dense
          selected={isOn}
        >
          <ListItemIcon>
            {isOn ? (
              <BookmarkOnIcon fontSize={"small"} color={"primary"} />
            ) : (
              <BookmarkOffIcon fontSize={"small"} />
            )}
          </ListItemIcon>
          <ListItemText
            primary={field.label}
            primaryTypographyProps={{ fontSize: "0.85rem" }}
          />
        </MenuItem>
      );
    });
    if (groupIdx < groups.length - 1) {
      items.push(<Divider key={`div-${group}`} sx={{ my: 0.5 }} />);
    }
  });
  return items;
};

const CitedInChips = ({ citedIn }) => {
  if (!citedIn || citedIn.length === 0) return null;
  return (
    <Stack
      direction={"row"}
      spacing={0.5}
      flexWrap={"wrap"}
      useFlexGap
      sx={{ mt: 0.25 }}
    >
      {citedIn.map((key) => {
        const field = findCitationField(key);
        return (
          <Chip
            key={key}
            label={field ? field.label : key}
            size={"small"}
            color={"primary"}
            variant={"outlined"}
            sx={{ fontSize: "0.625rem", height: 20 }}
          />
        );
      })}
    </Stack>
  );
};

const SourceCard = ({ source, onCiteSource, isUpdating }) => {
  const [citationAnchor, setCitationAnchor] = useState(null);
  const isCited = source.cited || false;
  const citedIn = source.citedIn || [];

  const handleToggleCited = () => {
    onCiteSource(source.url, !isCited, !isCited ? citedIn : []);
  };

  const handleToggleCitedIn = (fieldKey) => {
    const next = citedIn.includes(fieldKey)
      ? citedIn.filter((k) => k !== fieldKey)
      : [...citedIn, fieldKey];
    onCiteSource(source.url, next.length > 0, next);
    setCitationAnchor(null);
  };

  return (
    <Box
      sx={{
        py: 1,
        pl: isCited ? 1 : 0,
        borderLeft: isCited ? "2px solid" : "2px solid transparent",
        borderLeftColor: isCited ? "primary.main" : "transparent",
        transition: "all 0.15s",
        "&:not(:last-child)": {
          borderBottom: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      <Stack spacing={0.25}>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"flex-start"}
        >
          <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
            <Link
              href={source.url}
              target={"_blank"}
              rel={"noopener noreferrer"}
              underline={"hover"}
              sx={{
                fontWeight: 600,
                fontSize: "0.8125rem",
                lineHeight: 1.3,
                color: "primary.main",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {source.title || source.url}
              <OpenIcon sx={{ fontSize: 12, opacity: 0.5, flexShrink: 0 }} />
            </Link>
            {source.domain && (
              <Typography
                variant={"caption"}
                color={"text.disabled"}
                sx={{ fontSize: "0.7rem" }}
              >
                {source.domain}
              </Typography>
            )}
          </Stack>
          <Stack direction={"row"} spacing={0} sx={{ flexShrink: 0, ml: 0.5 }}>
            <Tooltip title={isCited ? "Remove citation" : "Cite this source"}>
              <IconButton
                size={"small"}
                onClick={handleToggleCited}
                disabled={isUpdating}
              >
                {isCited ? (
                  <BookmarkOnIcon sx={{ fontSize: 18 }} color={"primary"} />
                ) : (
                  <BookmarkOffIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={"Cite in field..."}>
              <IconButton
                size={"small"}
                onClick={(e) => setCitationAnchor(e.currentTarget)}
                disabled={isUpdating}
              >
                <LinkIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={citationAnchor}
              open={Boolean(citationAnchor)}
              onClose={() => setCitationAnchor(null)}
            >
              <CiteInFieldMenuItems
                citedIn={citedIn}
                onToggle={handleToggleCitedIn}
              />
            </Menu>
          </Stack>
        </Stack>
        {source.snippet && (
          <Typography
            variant={"body2"}
            color={"text.secondary"}
            sx={{ fontSize: "0.8rem", lineHeight: 1.45 }}
          >
            {source.snippet}
          </Typography>
        )}
        <CitedInChips citedIn={citedIn} />
      </Stack>
    </Box>
  );
};

const SourcesList = ({ sources, onCiteSource, isUpdating }) => {
  const [expanded, setExpanded] = useState(false);
  if (!sources || sources.length === 0) return null;

  const visibleCount = 3;
  const hasMore = sources.length > visibleCount;
  const displayed = expanded ? sources : sources.slice(0, visibleCount);

  return (
    <Box sx={{ mt: 0.5 }}>
      {displayed.map((source, idx) => (
        <SourceCard
          key={idx}
          source={source}
          onCiteSource={onCiteSource}
          isUpdating={isUpdating}
        />
      ))}
      {hasMore && (
        <Chip
          label={
            expanded
              ? "Show less"
              : `+${sources.length - visibleCount} more results`
          }
          size={"small"}
          variant={"outlined"}
          onClick={() => setExpanded(!expanded)}
          sx={{ mt: 0.5, fontSize: "0.7rem" }}
        />
      )}
    </Box>
  );
};

const FilingSections = ({ sections, archiveUrl }) => {
  if (!sections || sections.length === 0) return null;
  return (
    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
      {sections.map((section) => (
        <Accordion
          key={section.anchor}
          disableGutters
          elevation={0}
          square
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            "&:before": { display: "none" },
            "&.Mui-expanded": { margin: 0 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon fontSize={"small"} />}
            sx={{
              minHeight: 36,
              px: 1,
              "& .MuiAccordionSummary-content": { my: 0.5 },
            }}
          >
            <Stack direction={"row"} spacing={0.75} alignItems={"center"}>
              <Chip
                label={`Item ${section.anchor}`}
                size={"small"}
                variant={"outlined"}
                sx={{ fontSize: "0.65rem", height: 18 }}
              />
              <Typography variant={"caption"} fontWeight={600}>
                {section.title}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1, py: 0.75 }}>
            <Stack spacing={0.75}>
              <Typography
                variant={"caption"}
                component={"pre"}
                sx={{
                  fontSize: "0.75rem",
                  lineHeight: 1.5,
                  color: "text.secondary",
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                  m: 0,
                }}
              >
                {section.text}
              </Typography>
              {(section.url || archiveUrl) && (
                <Chip
                  component={Link}
                  href={section.url || archiveUrl}
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                  clickable
                  size={"small"}
                  variant={"outlined"}
                  icon={<OpenIcon sx={{ fontSize: 14 }} />}
                  label={"Read more on EDGAR"}
                  sx={{ alignSelf: "flex-start", fontSize: "0.7rem" }}
                />
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
};

const FollowUpComposer = ({ onFollowUp, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [includeContext, setIncludeContext] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    onFollowUp(trimmed, { includeSubmissionContext: includeContext });
    setQuery("");
    setOpen(false);
  };

  if (!open) {
    return (
      <Chip
        icon={<FollowUpIcon sx={{ fontSize: 14 }} />}
        label={"Ask a follow-up"}
        size={"small"}
        variant={"outlined"}
        clickable
        disabled={disabled}
        onClick={() => setOpen(true)}
        sx={{ alignSelf: "flex-start", fontSize: "0.7rem", mt: 0.5 }}
      />
    );
  }

  return (
    <Box component={"form"} onSubmit={handleSubmit} sx={{ mt: 0.5 }}>
      <Stack spacing={0.5}>
        <TextField
          autoFocus
          multiline
          minRows={2}
          maxRows={5}
          size={"small"}
          placeholder={"Ask a follow-up question..."}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
        />
        <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
          <Tooltip
            title={
              includeContext
                ? "Sending submission program data with this follow-up"
                : "Include submission program data with this follow-up"
            }
          >
            <IconButton
              size={"small"}
              onClick={() => setIncludeContext((v) => !v)}
              color={includeContext ? "primary" : "default"}
            >
              <IncludeContextIcon fontSize={"small"} />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Chip
            label={"Cancel"}
            size={"small"}
            variant={"outlined"}
            onClick={() => {
              setOpen(false);
              setQuery("");
            }}
            sx={{ fontSize: "0.7rem" }}
          />
          <Chip
            icon={<SendIcon sx={{ fontSize: 14 }} />}
            label={"Ask"}
            size={"small"}
            color={"primary"}
            disabled={disabled || !query.trim()}
            onClick={handleSubmit}
            sx={{ fontSize: "0.7rem" }}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

const ConversationThread = ({ messages }) => {
  if (!messages || messages.length <= 2) return null;
  // First two messages already render via the existing query+resultText path;
  // anything past that is the conversation thread.
  const followUps = messages.slice(2);
  return (
    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
      <Divider />
      {followUps.map((message, idx) => {
        const isUser = message.role === "user";
        return (
          <Box
            key={idx}
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: isUser ? "action.hover" : "transparent",
              borderLeft: isUser ? "none" : "2px solid",
              borderLeftColor: "secondary.main",
              pl: isUser ? 1 : 1.25,
            }}
          >
            <Typography
              variant={"caption"}
              fontWeight={600}
              color={"text.secondary"}
            >
              {isUser ? "You" : "AI"}
            </Typography>
            <Box
              sx={{
                fontSize: "0.8125rem",
                lineHeight: 1.55,
                color: "text.secondary",
                "& p": { my: 0.5 },
                "& strong": { color: "text.primary" },
                "& ul, & ol": { pl: 2, my: 0.5 },
                "& li": { mb: 0.25 },
              }}
            >
              <ReactMarkdown>
                {isUser
                  ? // strip out the synthetic context-block prefix we send on the
                    // wire so the user sees just the question they typed
                    message.content
                      .split("\n\n")[0]
                      .replace(/^Question:\s*/, "")
                  : message.content}
              </ReactMarkdown>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

const ResearchItemCard = ({
  item,
  onUpdate,
  onCiteSource,
  onDelete,
  onSaveAttachment,
  onFollowUp,
  isUpdating,
}) => {
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteText, setNoteText] = useState(item.note || "");
  const [citationAnchor, setCitationAnchor] = useState(null);
  const [overflowAnchor, setOverflowAnchor] = useState(null);

  const handleToggleCited = () => {
    onUpdate({
      isCited: !item.isCited,
      citedIn: !item.isCited ? item.citedIn : [],
    });
  };

  const handleSaveNote = () => {
    onUpdate({ note: noteText });
    setIsEditingNote(false);
  };

  const handleToggleCitedIn = (fieldKey) => {
    const current = item.citedIn || [];
    const next = current.includes(fieldKey)
      ? current.filter((k) => k !== fieldKey)
      : [...current, fieldKey];
    onUpdate({ isCited: next.length > 0, citedIn: next });
    setCitationAnchor(null);
  };

  const formattedDate = item.createdAt
    ? new Date(item.createdAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  const hasSources = item.sources && item.sources.length > 0;
  const isWebSearch = item.itemType === "web_search";
  const isAiQuery = item.itemType === "ai_query";
  const isReferenceFiling = item.itemType === "reference_filing";
  const filingSections = isReferenceFiling ? item.rawResponse?.sections : null;
  const filingArchiveUrl = isReferenceFiling
    ? item.rawResponse?.archive_url || item.rawResponse?.archiveUrl
    : null;
  const filingAttachmentId = isReferenceFiling
    ? item.rawResponse?.attachment_id || item.rawResponse?.attachmentId
    : null;
  const filingAttachmentFilename = isReferenceFiling
    ? item.rawResponse?.attachment_filename ||
      item.rawResponse?.attachmentFilename
    : null;
  const citedSourceCount = hasSources
    ? item.sources.filter((s) => s.cited).length
    : 0;

  return (
    <Paper
      variant={"outlined"}
      sx={{
        p: 1.5,
        borderLeft: item.isCited ? "3px solid" : "3px solid transparent",
        borderLeftColor: item.isCited ? "primary.main" : "transparent",
        transition: "border-color 0.2s",
      }}
    >
      <Stack spacing={0.75}>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Stack direction={"row"} spacing={0.75} alignItems={"center"}>
            <ItemTypeIcon itemType={item.itemType} />
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              fontWeight={500}
              sx={{ textTransform: "capitalize" }}
            >
              <ItemTypeLabel
                itemType={item.itemType}
                provider={item.provider}
              />
            </Typography>
            <Typography
              variant={"caption"}
              color={"text.disabled"}
              sx={{ fontSize: "0.7rem" }}
            >
              {formattedDate}
            </Typography>
            {isWebSearch && citedSourceCount > 0 && (
              <Chip
                label={`${citedSourceCount} cited`}
                size={"small"}
                color={"primary"}
                sx={{ fontSize: "0.65rem", height: 20 }}
              />
            )}
          </Stack>
          <Stack direction={"row"} spacing={0.25} alignItems={"center"}>
            {!isWebSearch && (
              <>
                <Tooltip
                  title={
                    item.isCited
                      ? "Bookmarked. Click to manage citation fields."
                      : "Bookmark or link this to a submission field"
                  }
                  enterDelay={500}
                >
                  <Chip
                    icon={
                      item.isCited ? (
                        <BookmarkOnIcon sx={{ fontSize: 14 }} />
                      ) : (
                        <BookmarkOffIcon sx={{ fontSize: 14 }} />
                      )
                    }
                    label={item.isCited ? "Cited" : "Cite"}
                    size={"small"}
                    clickable
                    color={item.isCited ? "primary" : "default"}
                    variant={item.isCited ? "filled" : "outlined"}
                    onClick={(e) => setCitationAnchor(e.currentTarget)}
                    disabled={isUpdating}
                    sx={{ fontSize: "0.7rem", height: 22 }}
                  />
                </Tooltip>
                <Menu
                  anchorEl={citationAnchor}
                  open={Boolean(citationAnchor)}
                  onClose={() => setCitationAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      handleToggleCited();
                      setCitationAnchor(null);
                    }}
                    dense
                  >
                    <ListItemIcon>
                      {item.isCited ? (
                        <BookmarkOnIcon fontSize={"small"} color={"primary"} />
                      ) : (
                        <BookmarkOffIcon fontSize={"small"} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.isCited ? "Remove bookmark" : "Bookmark"}
                    />
                  </MenuItem>
                  <Divider sx={{ my: 0.5 }} />
                  <CiteInFieldMenuItems
                    citedIn={item.citedIn || []}
                    onToggle={handleToggleCitedIn}
                  />
                </Menu>
              </>
            )}
            <Tooltip title={"More"} enterDelay={500}>
              <IconButton
                size={"small"}
                onClick={(e) => setOverflowAnchor(e.currentTarget)}
                disabled={isUpdating}
              >
                <MoreIcon fontSize={"small"} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={overflowAnchor}
              open={Boolean(overflowAnchor)}
              onClose={() => setOverflowAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setIsEditingNote(!isEditingNote);
                  setOverflowAnchor(null);
                }}
                dense
              >
                <ListItemIcon>
                  <NoteIcon fontSize={"small"} />
                </ListItemIcon>
                <ListItemText primary={item.note ? "Edit note" : "Add note"} />
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  onDelete();
                  setOverflowAnchor(null);
                }}
                dense
              >
                <ListItemIcon>
                  <DeleteIcon fontSize={"small"} color={"error"} />
                </ListItemIcon>
                <ListItemText primary={"Delete"} />
              </MenuItem>
            </Menu>
          </Stack>
        </Stack>

        {item.query && (
          <Typography
            variant={"body2"}
            fontWeight={600}
            sx={{ fontSize: "0.875rem" }}
          >
            {item.query}
          </Typography>
        )}

        {isWebSearch && hasSources && (
          <Typography variant={"caption"} color={"text.secondary"}>
            {`${item.sources.length} results`}
          </Typography>
        )}

        {isWebSearch && hasSources ? (
          <SourcesList
            sources={item.sources}
            onCiteSource={onCiteSource}
            isUpdating={isUpdating}
          />
        ) : isReferenceFiling ? (
          <>
            {item.resultText && (
              <Box
                sx={{
                  fontSize: "0.8125rem",
                  lineHeight: 1.6,
                  color: "text.secondary",
                  "& p": { my: 0.5 },
                  "& strong": { color: "text.primary" },
                  "& a": { color: "primary.main" },
                }}
              >
                <ReactMarkdown
                  components={{
                    a: ({ node, ...props }) => (
                      <Link
                        {...props}
                        target={"_blank"}
                        rel={"noopener noreferrer"}
                        underline={"hover"}
                      />
                    ),
                  }}
                >
                  {item.resultText}
                </ReactMarkdown>
              </Box>
            )}
            <FilingSections
              sections={filingSections}
              archiveUrl={filingArchiveUrl}
            />
          </>
        ) : (
          <>
            {item.resultText && (
              <Box
                sx={{
                  fontSize: "0.8125rem",
                  lineHeight: 1.6,
                  color: "text.secondary",
                  "& h1, & h2, & h3": {
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "text.primary",
                    mt: 1.5,
                    mb: 0.5,
                  },
                  "& p": { my: 0.5 },
                  "& ul, & ol": { pl: 2, my: 0.5 },
                  "& li": { mb: 0.25 },
                  "& strong": { color: "text.primary" },
                  "& a": { color: "primary.main" },
                }}
              >
                <ReactMarkdown
                  components={{
                    a: ({ node, ...props }) => (
                      <Link
                        {...props}
                        target={"_blank"}
                        rel={"noopener noreferrer"}
                        underline={"hover"}
                      />
                    ),
                  }}
                >
                  {item.resultText}
                </ReactMarkdown>
              </Box>
            )}
            {hasSources && (
              <>
                <Divider sx={{ my: 0.5 }} />
                <Typography
                  variant={"caption"}
                  color={"text.secondary"}
                  sx={{ fontWeight: 600 }}
                >
                  {`Sources (${item.sources.length})`}
                </Typography>
                <SourcesList
                  sources={item.sources}
                  onCiteSource={onCiteSource}
                  isUpdating={isUpdating}
                />
              </>
            )}
          </>
        )}

        {isReferenceFiling && onSaveAttachment && (
          <Tooltip
            title={
              filingAttachmentId
                ? `Saved as ${filingAttachmentFilename || "attachment"}`
                : "Save the original EDGAR filing to this submission's attachments"
            }
            enterDelay={500}
          >
            <Chip
              icon={
                filingAttachmentId ? (
                  <CheckIcon sx={{ fontSize: 14 }} />
                ) : (
                  <AttachIcon sx={{ fontSize: 14 }} />
                )
              }
              label={
                filingAttachmentId
                  ? "Saved to attachments"
                  : "Save to attachments"
              }
              size={"small"}
              clickable={!filingAttachmentId}
              onClick={!filingAttachmentId ? onSaveAttachment : undefined}
              disabled={isUpdating}
              color={filingAttachmentId ? "success" : "default"}
              variant={filingAttachmentId ? "filled" : "outlined"}
              sx={{ alignSelf: "flex-start", fontSize: "0.7rem", mt: 0.5 }}
            />
          </Tooltip>
        )}

        {isAiQuery && <ConversationThread messages={item.messages} />}
        {isAiQuery && onFollowUp && (
          <FollowUpComposer onFollowUp={onFollowUp} disabled={isUpdating} />
        )}

        {!isWebSearch && item.citedIn && item.citedIn.length > 0 && (
          <>
            <Divider sx={{ my: 0.25 }} />
            <CitedInChips citedIn={item.citedIn} />
          </>
        )}

        {item.note && !isEditingNote && (
          <Box
            sx={{ bgcolor: "action.hover", borderRadius: 1, px: 1, py: 0.75 }}
          >
            <Typography
              variant={"caption"}
              fontWeight={600}
              color={"text.secondary"}
            >
              {"Note: "}
            </Typography>
            <Typography variant={"caption"}>{item.note}</Typography>
          </Box>
        )}

        <Collapse in={isEditingNote}>
          <Stack spacing={1}>
            <TextField
              multiline
              minRows={2}
              maxRows={6}
              size={"small"}
              fullWidth
              placeholder={"Add your notes about this research..."}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <Stack direction={"row"} spacing={1} justifyContent={"flex-end"}>
              <Chip
                label={"Cancel"}
                size={"small"}
                onClick={() => {
                  setNoteText(item.note || "");
                  setIsEditingNote(false);
                }}
              />
              <Chip
                label={"Save Note"}
                size={"small"}
                color={"primary"}
                onClick={handleSaveNote}
                disabled={isUpdating}
              />
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
};

export default ResearchItemCard;
